import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AccessProvider, LOADING_STATES, useAccess } from "./contexts/AccessContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { RadioPlayerProvider } from "./contexts/RadioPlayerContext";
import { FeatureGate } from "./components/FeatureGate";
import { getBackendUrl } from "./utils/api";
import ImagePreview from "./pages/ImagePreview";
import AdminRadioChartUpload from "./components/AdminRadioChartUpload";
import RadioTopSongs from "./components/RadioTopSongs";
import RadioTopSongsPreview from "./components/RadioTopSongsPreview";
import OurStations from "./components/OurStations";
import StreamMonitor from "./components/StreamMonitor/index";
import RankingDiagnostics from "./pages/admin/RankingDiagnostics";
import EmailUnsubscribers from "./pages/admin/EmailUnsubscribers";
import ClassifierReport from "./pages/admin/ClassifierReport";
import BreakIntelligence from "./pages/admin/BreakIntelligence";
import ClockEventIntegrity from "./pages/admin/ClockEventIntegrity";
import AdminLayeredTelemetryDashboard from "./components/AdminLayeredTelemetryDashboard";
// FloatingRadioPlayer removed - using BottomRadioPlayer instead
import BottomRadioPlayer from "./components/BottomRadioPlayer";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Calendar, Download, Radio, Sparkles, RefreshCw, Clock, LogOut, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import Login from "./components/Login";
import Signup from "./components/Signup";
import ScrollToTop from "./components/ScrollToTop";
import ScrollNav from "./components/ScrollNav";
import TrialSignup from "./components/TrialSignup";
import AuthCallback from "./pages/AuthCallback";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import AdminDashboard from "./components/AdminDashboard";
import AIDiagnosticsPage from "./pages/AIDiagnosticsPage";
import RollbackManager from "./components/RollbackManager";
import BackupManager from "./components/BackupManager";
import SongInfo from "./components/SongInfo";
import CampaignCalculator from "./pages/CampaignCalculator";
import ListenerCalculator from "./pages/ListenerCalculator";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";
import ContactForm from "./components/ContactForm";
import AIChatbot from "./components/AIChatbot";
import SubscriptionAlert from "./components/SubscriptionAlert";
import RadioClock from "./components/RadioClock";
import WelcomeGuide from "./components/WelcomeGuide";
import WelcomePopup from "./components/WelcomePopup";
import CookieBanner from "./components/CookieBanner";
import Footer from "./components/Footer";
import TopNavBar from "./components/TopNavBar";
import MaintenanceScreen from "./components/MaintenanceScreen";
import MaintenanceDemo from "./components/MaintenanceDemo";
import DateNavigator from "./components/DateNavigator";
import LanguageSwitcher from "./components/LanguageSwitcher";
import SEODashboard from "./pages/SEODashboard";
import AboutPage from "./pages/AboutPage";
import OnAirContentPage from "./pages/OnAirContentPage";
import SongInfoPage from "./pages/SongInfoPage";
import ServicesPage from "./pages/ServicesPage";
import EmailCapturePopup from "./components/EmailCapturePopup";
import SecurityProvider from "./components/SecurityProvider";
import SongsAnalyzerPage from "./pages/SongsAnalyzerPage";
import RadioChartPage from "./pages/RadioChartPage";
import TopArtistsPage from "./pages/TopArtistsPage";

import CompareStationsPage from "./pages/CompareStationsPage";
import C1Diagnostics from "./pages/C1Diagnostics";
import { getTotalSections } from "./config/sectionsConfig";
import storage from "./utils/storage";
import cacheManager from "./utils/cacheManager";

// Dynamic backend URL detection
const getBackendURL = () => {
  // Use the centralized API configuration utility
  return getBackendUrl();
};

const BACKEND_URL = getBackendURL();
const API = `${BACKEND_URL}/api`;

// Helper function to clean content from date markers and system messages
const cleanContent = (text) => {
  if (!text) return text;
  let cleaned = text;
  // Remove standalone "i" characters that appear before content info lines
  cleaned = cleaned.replace(/^i\s*$/gm, '');
  cleaned = cleaned.replace(/\ni\s*\n/g, '\n');
  // Remove ALL fallback marker patterns - comprehensive cleanup
  // Pattern 1: "ℹ️ *Περιεχόμενο από YYYY-MM-DD*" (with emoji)
  cleaned = cleaned.replace(/[ℹ️📅🔄]\s*\*?Περιεχόμενο από[^*\n]*\*?\n*/gi, '');
  // Pattern 2: "_Περιεχόμενο από: YYYY-MM-DD_" (underscored version)
  cleaned = cleaned.replace(/_Περιεχόμενο από[^_]*_\n*/g, '');
  // Pattern 3: "*Περιεχόμενο από YYYY-MM-DD*" (asterisk version)
  cleaned = cleaned.replace(/\*Περιεχόμενο από[^*]*\*\n*/g, '');
  // Pattern 4: "i *Περιεχόμενο από YYYY-MM-DD*" patterns
  cleaned = cleaned.replace(/i\s*\*Περιεχόμενο από[^*]*\*\n*/g, '');
  // Pattern 5: "Περιεχόμενο από YYYY-MM-DD" patterns (plain version)
  cleaned = cleaned.replace(/Περιεχόμενο από \d{4}-\d{2}-\d{2}\n*/g, '');
  // Pattern 6: Lines starting with info emoji followed by Περιεχόμενο
  cleaned = cleaned.replace(/^.*Περιεχόμενο από \d{4}-\d{2}-\d{2}.*$/gm, '');
  // Remove "Προσωρινό Περιεχόμενο" messages
  cleaned = cleaned.replace(/Προσωρινό Περιεχόμενο[^.]*\./g, '');
  // Remove "Χρησιμοποιούμε περιεχόμενο από χθες" messages
  cleaned = cleaned.replace(/Χρησιμοποιούμε περιεχόμενο από[^.]*\./g, '');
  // Remove lines that are just "i" followed by date info patterns
  cleaned = cleaned.replace(/^i\s+\*?Περιεχόμενο.*$/gm, '');
  // Remove multiple consecutive newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
};

// Safe Image component that hides broken images completely
const SafeImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  
  // Don't render if no valid src
  if (!src || src.trim() === '' || src === 'null' || src === 'undefined' || hasError) {
    return null;
  }
  
  return (
    <img 
      src={src} 
      alt={alt || ''} 
      className={`${className} ${isLoaded ? '' : 'opacity-0'}`}
      onError={() => setHasError(true)}
      onLoad={() => setIsLoaded(true)}
      loading="lazy"
      style={{ display: hasError ? 'none' : 'block' }}
    />
  );
};

// Access Loading Indicator Component - Shows friendly loading messages during authentication
const AccessLoadingIndicator = () => {
  const { loadingState, retryCount, loading } = useAccess();
  const { t } = useLanguage();
  
  // Only show when loading and not idle
  if (!loading || loadingState === LOADING_STATES.IDLE) {
    return null;
  }
  
  const getMessage = () => {
    switch (loadingState) {
      case LOADING_STATES.LOADING:
        return t('accessLoading');
      case LOADING_STATES.SLOW:
        return t('accessSlow');
      case LOADING_STATES.RETRYING:
        return t('accessRetrying').replace('{count}', retryCount);
      case LOADING_STATES.USING_CACHE:
        return t('accessUsingCache');
      case LOADING_STATES.ERROR:
        return t('accessError');
      default:
        return t('accessLoading');
    }
  };
  
  const getIcon = () => {
    switch (loadingState) {
      case LOADING_STATES.ERROR:
        return '⚠️';
      case LOADING_STATES.USING_CACHE:
        return '📦';
      default:
        return null;
    }
  };
  
  const icon = getIcon();
  
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="bg-white/95 backdrop-blur-sm border border-purple-200 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 max-w-md">
        {icon ? (
          <span className="text-xl">{icon}</span>
        ) : (
          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
        )}
        <p className="text-sm text-gray-700">{getMessage()}</p>
      </div>
    </div>
  );
};

// Email Capture Popup Wrapper - Only shows on /about
const EmailCapturePopupWrapper = ({ isAuthenticated }) => {
  const location = useLocation();
  const showPopup = location.pathname === '/about' && !isAuthenticated;
  
  if (!showPopup) return null;
  return <EmailCapturePopup />;
};

// External redirect for non-logged users
const ExternalAboutRedirect = () => {
  React.useEffect(() => {
    // Use relative URL so preview/staging environments don't bounce to prod
    window.location.href = "/about";
  }, []);
  return null;
};

// Guard for authenticated users whose access has expired (trial ended,
// subscription expired, or simply no package). They get redirected to
// /pricing instead of seeing a blurred locked page forever.
// Admin/trial/free_access/subscriber are all allowed through.
const ExpiredAccessGuard = ({ children }) => {
  const { access, loading } = useAccess();
  const location = useLocation();

  // While still fetching, render children (FeatureGate below will still
  // enforce feature-level access).
  if (loading) return children;

  const accessType = access?.access_type;
  const blockedTypes = ['none', 'expired'];

  if (blockedTypes.includes(accessType)) {
    // Avoid redirect loop when already on pricing
    if (!location.pathname.startsWith('/pricing') &&
        !location.pathname.startsWith('/syndromes') &&
        !location.pathname.startsWith('/paketa')) {
      return <Navigate to="/pricing" replace />;
    }
  }
  return children;
};

function App() {
  // Authentication state - moved from Home component
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // NEW: Loading state
  
  // Theme state for Dark/Light mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
    } catch (e) {
      // localStorage not available (e.g., in iframe)
    }
    // Check system preference
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (e) {
      return true; // Default to dark mode
    }
  });

  // Initialize theme on mount
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Check authentication on mount
  useEffect(() => {
    console.log('🔍 [App] Checking authentication on mount...');
    const token = storage.getItem('token');
    const savedUser = storage.getItem('user');
    
    console.log('🔍 [App] Token:', token ? 'EXISTS' : 'NULL');
    console.log('🔍 [App] Saved User:', savedUser ? 'EXISTS' : 'NULL');
    
    if (token && savedUser) {
      try {
        // 2026-11-01: an expired login used to look "logged in" while every request failed.
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          throw new Error('token expired');
        }
        const parsedUser = JSON.parse(savedUser);
        console.log('✅ [App] Setting authenticated state, user:', parsedUser.username);
        setCurrentUser(parsedUser);
        setIsAuthenticated(true);
      } catch (e) {
        console.error('❌ [App] Failed to parse user data:', e);
        storage.removeItem('token');
        storage.removeItem('user');
        if (e.message === 'token expired' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?expired=1';
        }
      }
    } else {
      console.log('⚠️ [App] No token or user found, staying unauthenticated');
    }
    
    // Mark auth check as complete
    setAuthLoading(false);
  }, []);

  // Handle logout
  const handleLogout = () => {
    storage.removeItem('token');
    storage.removeItem('user');
    setCurrentUser(null);
    setIsAuthenticated(false);
    window.location.href = '/about';
  };

  // Handle successful login (from Login component or Google OAuth)
  const handleLoginSuccess = (userData) => {
    setCurrentUser(userData);
    setIsAuthenticated(true);
  };

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F6FB] via-[#F5F0FA] to-[#F0EBF8] dark:from-[#1A0730] dark:via-[#2D0C4B] dark:to-[#1A0730]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  return (
    <SecurityProvider enabled={true} isAdmin={currentUser?.role === 'admin'}>
    <HelmetProvider>
    <div className="App">
      <ThemeProvider>
      <LanguageProvider>
      <BrowserRouter>
      <ScrollToTop />
      <ScrollNav />
      <RadioPlayerProvider>
      <AccessProvider user={currentUser}>
        {/* Access Loading Indicator - Shows friendly messages during auth */}
        <AccessLoadingIndicator />
        
        {/* Email Capture Popup - Shows on about page for non-authenticated users only */}
        <EmailCapturePopupWrapper isAuthenticated={isAuthenticated} />
        
        {/* Global Top Navigation Bar - Shows on all pages */}
        <TopNavBar 
          isAuthenticated={isAuthenticated} 
          currentUser={currentUser}
          onLogout={handleLogout}
        />
        
        <Routes>
          {/* Root path always redirects to /about */}
          <Route path="/" element={<Navigate to="/about" replace />} />
          <Route path="/about" element={<AboutPage isAuthenticated={isAuthenticated} currentUser={currentUser} />} />
          <Route path="/on-air-content" element={<OnAirContentPage isAuthenticated={isAuthenticated} currentUser={currentUser} />} />
          <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/trial-signup" element={<TrialSignup />} />
          <Route path="/auth/callback" element={<AuthCallback onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          {/* Protected Routes - Require login AND active subscription */}
          {/* Campaign Analyzer - Greek and English routes */}
          <Route path="/metritis-kampanias" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="campaign_analyzer">
                  <CampaignCalculator />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          <Route path="/campaign-analyzer" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="campaign_analyzer">
                  <CampaignCalculator />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          {/* Listener Calculator - Greek and English routes */}
          <Route path="/metritis-akroaton" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="listener_calculator">
                  <ListenerCalculator />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          <Route path="/listener-calculator" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="listener_calculator">
                  <ListenerCalculator />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          {/* v2.13.53.1 — Removed routes per operator request:
              /playlist-analyzer and /daypart-analyzer pages removed. */}
          <Route path="/rollback" element={<RollbackManager />} />
          <Route path="/backup" element={
            isAuthenticated && currentUser?.role === 'admin' ? <BackupManager /> : <Navigate to="/login?redirect=/backup" replace />
          } />
          <Route path="/seo-images" element={<ImagePreview />} />
          <Route path="/admin" element={
            isAuthenticated && currentUser?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/login?redirect=/admin" replace />
          } />
          <Route path="/admin/radio-chart-upload" element={<AdminRadioChartUpload />} />
          <Route path="/admin/c1-diagnostics" element={<C1Diagnostics />} />
          <Route path="/admin/seo-dashboard" element={
            isAuthenticated && currentUser?.role === 'admin' ? <SEODashboard /> : <Navigate to="/" />
          } />
          <Route path="/admin/stream-monitor" element={
            isAuthenticated && currentUser?.role === 'admin' ? (
              <StreamMonitor token={storage.getItem('token')} />
            ) : <Navigate to="/" />
          } />
          <Route path="/admin/ranking-diagnostics" element={
            isAuthenticated && currentUser?.role === 'admin' ? <RankingDiagnostics /> : <Navigate to="/" />
          } />
          <Route path="/admin/ai-diagnostics" element={
            isAuthenticated && currentUser?.role === 'admin' ? <AIDiagnosticsPage isAuthenticated={isAuthenticated} currentUser={currentUser} /> : <Navigate to="/login?redirect=/admin/ai-diagnostics" replace />
          } />
          <Route path="/admin/email-unsubscribers" element={
            isAuthenticated && currentUser?.role === 'admin' ? <EmailUnsubscribers /> : <Navigate to="/login?redirect=/admin/email-unsubscribers" replace />
          } />
          <Route path="/admin/classifier-report" element={
            isAuthenticated && currentUser?.role === 'admin' ? <ClassifierReport /> : <Navigate to="/login?redirect=/admin/classifier-report" replace />
          } />
          <Route path="/admin/break-intelligence" element={
            isAuthenticated && currentUser?.role === 'admin' ? <BreakIntelligence /> : <Navigate to="/login?redirect=/admin/break-intelligence" replace />
          } />
          <Route path="/admin/clock-integrity" element={
            isAuthenticated && currentUser?.role === 'admin' ? <ClockEventIntegrity /> : <Navigate to="/login?redirect=/admin/clock-integrity" replace />
          } />
          <Route path="/admin/clock-integrity/:stationId" element={
            isAuthenticated && currentUser?.role === 'admin' ? <ClockEventIntegrity /> : <Navigate to="/login?redirect=/admin/clock-integrity" replace />
          } />
          <Route path="/admin/layered-telemetry" element={
            isAuthenticated && currentUser?.role === 'admin' ? (
              <div className="min-h-screen bg-gray-900 text-white p-6">
                <AdminLayeredTelemetryDashboard />
              </div>
            ) : <Navigate to="/login?redirect=/admin/layered-telemetry" replace />
          } />
          {/* Songs Analyzer - Subscriber access to Stream Monitor */}
          <Route path="/songs-analyzer" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="songs_analyzer">
                  <SongsAnalyzerPage user={currentUser} />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          <Route path="/songs-analyzer/:stationId" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="songs_analyzer">
                  <SongsAnalyzerPage user={currentUser} />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          <Route path="/radio-top-songs" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="radio_top_songs">
                  <RadioTopSongs />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
          <Route path="/our-stations" element={<OurStations />} />
          <Route path="/maintenance-demo" element={<MaintenanceDemo />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/song-info" element={<SongInfoPage />} />
          <Route path="/pricing" element={<PricingPage user={currentUser} />} />
          <Route path="/radio-chart" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="peoples_chart">
                  <RadioChartPage user={currentUser} />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />

          {/* Top Artists — public charts (Greek / Global) */}
          <Route path="/top-artists" element={<Navigate to="/top-artists/greek" replace />} />
          <Route path="/top-artists/:scope" element={<TopArtistsPage user={currentUser} />} />
          
          {/* Compare Stations - PRO feature */}
          <Route path="/compare-stations" element={
            isAuthenticated ? (
              <ExpiredAccessGuard>
                <FeatureGate featureId="compare_stations">
                  <CompareStationsPage user={currentUser} />
                </FeatureGate>
              </ExpiredAccessGuard>
            ) : <ExternalAboutRedirect />
          } />
        <Route path="/syndromes" element={<PricingPage user={currentUser} />} />
        <Route path="/paketa" element={<PricingPage user={currentUser} />} />
        </Routes>
        {/* Chatbot - Available only for paid users */}
        <AIChatbot user={currentUser} isAuthenticated={isAuthenticated} />
        {/* GDPR Cookie Banner - Available on all pages */}
        <CookieBanner />
        {/* Bottom Radio Player - Persistent across pages */}
        <BottomRadioPlayer />
        {/* Footer with GDPR Links */}
        <Footer isAuthenticated={isAuthenticated} user={currentUser} />
      </AccessProvider>
      </RadioPlayerProvider>
      </BrowserRouter>
      {/* Welcome Popup - DISABLED: About page is now the landing page for non-authenticated users */}
      {/* <WelcomePopup 
        isAuthenticated={isAuthenticated} 
        hasSubscription={currentUser?.subscription_status === 'active'}
      /> */}
      </LanguageProvider>
      </ThemeProvider>
      
      {/* Toast Notifications */}
      <Toaster />
    </div>
    </HelmetProvider>
    </SecurityProvider>
  );
}

export default App;
